import styled from "styled-components";
// Path assumes this file lives at
// src/components/sections/WelcomeScreen/Activity/ActivityLevelStep.styles.js
import { DIET_C } from "../../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVITY LEVEL STEP
// Reuses the --c-*/--sp-*/--r-sm/--t-fast custom properties that
// AssessmentStepShell.styles.js's <Section> scopes over the whole step —
// this file never redeclares them, same as LifestyleStep.styles.js.
// Selected-state matches GoalCard/WorkTypeCard: 2.4px DIET_C.primary
// border, no fill.
// ═══════════════════════════════════════════════════════════════════════════════

export const FieldGroup = styled.div`
  margin-bottom: var(--sp-6);
`;
FieldGroup.displayName = "FieldGroup";

export const FieldLabel = styled.label`
  display: block;
  color: var(--c-text);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: var(--sp-2);
`;
FieldLabel.displayName = "FieldLabel";

export const RequiredMark = styled.span`
  color: ${DIET_C.accent};
  margin-left: 2px;
`;
RequiredMark.displayName = "RequiredMark";

export const HelperValue = styled.span`
  color: var(--c-text-muted);
  font-weight: 500;
`;
HelperValue.displayName = "HelperValue";

export const HelperText = styled.p`
  margin: 0;
  color: var(--c-text-muted);
  font-size: 0.85rem;
  line-height: 1.5;
`;
HelperText.displayName = "HelperText";

// ─── Activity tier cards ────────────────────────────────────────────────────
export const TierGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--sp-3);

  @media (max-width: 60em) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 30em) {
    grid-template-columns: 1fr;
  }
`;
TierGrid.displayName = "TierGrid";

export const TierCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--sp-1);
  min-height: 100px;
  padding: var(--sp-4) var(--sp-3);
  border-radius: var(--r-sm);
  background: var(--c-surface);
  color: var(--c-text);
  cursor: pointer;
  border: 2.4px solid ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}66`)};
  transition: border-color var(--t-fast);

  &:hover {
    border-color: ${DIET_C.primary};
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent);
    outline-offset: 2px;
  }

  @media (max-width: 40em) {
    min-height: 88px;
  }
`;
TierCard.displayName = "TierCard";

export const TierLabel = styled.span`
  font-size: 0.92rem;
  font-weight: 700;
`;
TierLabel.displayName = "TierLabel";

export const TierDesc = styled.span`
  font-size: 0.76rem;
  color: var(--c-text-muted);
  line-height: 1.3;
`;
TierDesc.displayName = "TierDesc";

// ─── Exercise details — multi-select chips ─────────────────────────────────
export const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
`;
ChipGrid.displayName = "ChipGrid";

export const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 999px;
  background: var(--c-surface);
  color: var(--c-text);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 2.4px solid ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}66`)};
  transition: border-color var(--t-fast);

  i {
    font-size: 1rem;
  }

  &:hover {
    border-color: ${DIET_C.primary};
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent);
    outline-offset: 2px;
  }
`;
Chip.displayName = "Chip";

// ─── Frequency — Days/week + Minutes/day ───────────────────────────────────
export const FrequencyRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-4);

  @media (max-width: 30em) {
    grid-template-columns: 1fr;
  }
`;
FrequencyRow.displayName = "FrequencyRow";

export const DayPillRow = styled.div`
  display: flex;
  gap: var(--sp-2);
  flex-wrap: wrap;
`;
DayPillRow.displayName = "DayPillRow";

export const DayPill = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: var(--c-surface);
  color: var(--c-text);
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  border: 2.4px solid ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}66`)};
  transition: border-color var(--t-fast);

  &:hover {
    border-color: ${DIET_C.primary};
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent);
    outline-offset: 2px;
  }
`;
DayPill.displayName = "DayPill";

export const RangeInput = styled.input.attrs({ type: "range" })`
  width: 100%;
  accent-color: ${DIET_C.primary};
`;
RangeInput.displayName = "RangeInput";

export const RangeScaleRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--c-text-muted);
  margin-top: var(--sp-1);
`;
RangeScaleRow.displayName = "RangeScaleRow";